import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import PropTypes from "prop-types";
import Select from "react-select";
import { DatePicker, Select as CommonSelect, TimePicker } from "antd";
import dayjs from "dayjs";
import get from "lodash/get";
import { format } from "date-fns";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";

const Input = ({
  name,
  label,
  placeholder,
  type = "text",
  required = false,
  labelClassName = "",
  requiredMessage = "This field is required",
  optionData = [],
  defaultValue = "",
  dateFormat = "",
  onChange = () => {},

  ...rest
}) => {
  const {
    register,
    formState: { errors },
    control,
    setValue,
    trigger,
  } = useFormContext();

  const renderInfoLabel = () => (
    <div className="popover-list">
      <OverlayTrigger
        placement="top"
        // overlay={<Tooltip id="tooltip-example">{rest?.info}</Tooltip>}
        overlay={
          <Popover id="popover-basic">
            <Popover.Header as="h3">Info</Popover.Header>
            <Popover.Body>{rest?.info}</Popover.Body>
          </Popover>
        }
        delay={{ show: 250, hide: 400 }}
      >
        <label
          htmlFor={name}
          className={labelClassName}
          style={{ marginBottom: 0, marginRight: "0.5rem" }}
        >
          {label}
          {required && <span className="asterisk">*</span>}
          {/* <div>({rest?.info})</div> */}
          <i className="fas fa-info-circle ms-2" aria-hidden="true"></i>
        </label>
      </OverlayTrigger>
    </div>
  );

  const renderLabel = () =>
    rest?.info ? (
      renderInfoLabel()
    ) : (
      <label htmlFor={name} className={labelClassName}>
        {label}
        {required && <span className="asterisk">*</span>}
      </label>
    );

  const renderTextInput = () => {
    if (type === "number") {
      return (
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue ?? ""}
          rules={{ required: required ? requiredMessage : false }}
          render={({ field }) => (
            <input
              {...field}
              id={name}
              type="number"
              placeholder={placeholder}
              value={
                field.value === undefined || field.value === null
                  ? ""
                  : field.value
              }
              onChange={(e) => {
                const val = e.target.value;
                const maxLen = rest?.maxLength || 10;

                // Preserve empty string when user clears the input instead of converting to 0
                if (val === "") {
                  field.onChange("");
                  return;
                }

                // Allow intermediate inputs like "-", ".", or "-." without converting to Number
                if (val === "-" || val === "." || val === "-.") {
                  field.onChange(val);
                  return;
                }

                // If maxLength provided, enforce it on the input string form
                if (maxLen && val.length > maxLen) {
                  const truncated = val.slice(0, maxLen);
                  // Avoid converting to Number when truncated results in "-" or "." or "-."
                  if (
                    truncated === "-" ||
                    truncated === "." ||
                    truncated === "-."
                  ) {
                    field.onChange(truncated);
                  } else {
                    const parsed = Number(truncated);
                    field.onChange(Number.isNaN(parsed) ? truncated : parsed);
                  }
                } else {
                  const parsed = Number(val);
                  field.onChange(Number.isNaN(parsed) ? val : parsed);
                }
              }}
              // onWheel={(e) => e.preventDefault()}
              onWheel={(e) => e.target.blur()} // prevents number scroll change
              style={{ MozAppearance: "textfield" }}
              {...rest}
            />
          )}
        />
      );
    }

    return (
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        {...register(name, {
          required: required ? requiredMessage : false,
        })}
        {...rest}
      />
    );
  };

  const renderSelectInput = () => (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ""}
      render={({ field }) => (
        <Select
          {...field}
          options={optionData}
          placeholder={placeholder}
          value={
            optionData.find((option) => option.value === field.value) || ""
          }
          onChange={(selectedOption) => field.onChange(selectedOption.value)}
          styles={{
            control: (base) => ({
              ...base,
              border: "none",
              borderBottom: "1px solid #ced4da",
              borderRadius: "4px",
              boxShadow: "none",
              "&:hover": { borderColor: "#80bdff" },
              minHeight: "60px",
            }),
          }}
          {...rest}
        />
      )}
    />
  );

  const renderCommonSelectInput = () => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <CommonSelect
          {...field}
          showSearch
          options={optionData}
          // placeholder={!field.value && placeholder ? placeholder : undefined}
          // value={
          //   field.value !== undefined && field.value !== ""
          //     ? field.value
          //     : undefined
          // }
          onChange={(value) => field.onChange(value)}
          size="large"
          style={{ width: "100%" }}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          {...rest}
        />
      )}
    />
  );

  const renderCommonSelectInput2 = () => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <CommonSelect
          {...field}
          showSearch
          options={optionData}
          placeholder={placeholder}
          // value={
          //   field.value !== undefined && field.value !== ""
          //     ? field.value
          //     : undefined
          // }
          // onChange={(value) => field.onChange(value)}
          size="large"
          style={{ width: "100%" }}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          {...rest}
        />
      )}
    />
  );

  const renderTimePickerInput = () => (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ""}
      render={({ field }) => (
        <TimePicker
          {...field}
          {...rest}
          onChange={(time, timeString) => {
            setValue(name, timeString);
            trigger(name);
          }}
        />
      )}
    />
  );

  const renderDateInput = () => (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ""}
      render={({ field }) => (
        <DatePicker
          {...field}
          placeholder={placeholder}
          value={field.value ? dayjs(field.value, dateFormat) : null}
          format={dateFormat}
          onChange={(date, dateString) => {
            setValue(name, date ? dayjs(date).format(dateFormat) : "");
            trigger(name);
          }}
          {...rest}
        />
      )}
    />
  );

  const renderError = (name) => {
    const error = get(errors, name);
    return error ? (
      <div className="invalid-feedback">{error.message}</div>
    ) : null;
  };

  return (
    <div className="form-floating">
      {rest?.info && renderInfoLabel()}
      {type === "select"
        ? renderSelectInput()
        : type === "date"
        ? renderDateInput()
        : type === "commonSelect"
        ? renderCommonSelectInput()
        : type === "commonSelect2"
        ? renderCommonSelectInput2()
        : type === "timePicker"
        ? renderTimePickerInput()
        : renderTextInput()}
      {!rest?.info &&
        (type !== "select" ||
          type !== "commonSelect" ||
          type !== "commonSelect2") &&
        label !== "" &&
        renderLabel()}
      {renderError(name)}
    </div>
  );
};

Input.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string.isRequired,
  required: PropTypes.bool,
  labelClassName: PropTypes.string,
  requiredMessage: PropTypes.string,
  optionData: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.bool,
      ]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  dateFormat: PropTypes.string,
  defaultValue: PropTypes.any,
  onChange: PropTypes.func,
  labelInValue: PropTypes.bool,
  value: PropTypes.any,
};

export default Input;
